import { useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'
import { isEmpty } from 'lodash'
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp'
import { Box, Divider, Grid, ListItemButton, Stack, Typography } from '@mui/material'
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion'
import { styled, useTheme } from '@mui/material/styles'
import MuiAccordionSummary, { AccordionSummaryProps } from '@mui/material/AccordionSummary'
import MuiAccordionDetails from '@mui/material/AccordionDetails'
import { CWIExplain } from 'fs_design_system'
import FsDetailedDescriptiveOverview from '../fs-detailed-descriptive-overview/fs-detailed-descriptive-overview'
import FsDescriptionByDiversity from '../fs-detailed-descriptions/fs-description-by-diversity'
import { DiversityDimenstionConfig, ModuleConfig } from '../../shared/model'
import FsDescriptionByCauses from '../fs-detailed-descriptions/fs-description-by-causes'
import FsDescriptionByColumnPercentage from '../fs-detailed-descriptions/fs-description-by-columnPercentage'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { setDetailedGapDescription } from '../../store/actions'
import { GraphSeries } from '../fs-gap-cause/fs-gap-cause'
import i18n from '../../i18n'
import { getIcon } from '../common/Icons'
import { translatedDomainName } from '../../shared/utils'
import FsFullPageLoader from '../common/atoms/FullPageLoader'

type FsGapDescriptionProps = {
  gapId: number
  empiricalConceptId: number
  moduleConfig?: ModuleConfig
  diversityDimenstionConfig?: DiversityDimenstionConfig
  gapGraphSeries: GraphSeries[]
  groupOfInterestPercentage: number
}

const FsDetailDescription = (props: FsGapDescriptionProps) => {
  const {
    gapId,
    empiricalConceptId,
    moduleConfig,
    diversityDimenstionConfig,
    gapGraphSeries,
    groupOfInterestPercentage,
  } = props
  const { detailedGapDescription, currentRun } = useAppSelector((state) => state.currentAnalysis)
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [curId, setCurId] = useState<number>(empiricalConceptId)
  const [id, setId] = useState(`${gapId}_${empiricalConceptId}`)
  const [expanded, setExpanded] = useState('panel4')
  const theme = useTheme()

  useEffect(() => {
    if (currentRun && (!detailedGapDescription || isEmpty(detailedGapDescription[id]))) {
      dispatch(setDetailedGapDescription({ gapId, empiricalConceptId: curId }))
    }
  }, [gapId, curId, currentRun])

  const gapDescription = detailedGapDescription ? detailedGapDescription[id] : null

  const Accordion = styled((aprops: AccordionProps) => (
    <MuiAccordion disableGutters elevation={0} square {...aprops} />
  ))(() => ({
    border: `1px solid ${moduleConfig?.fsColor.light}66`,
    '&:first-of-type': {
      borderTopLeftRadius: 8,
      borderTopRightRadius: 8,
    },
    '&:not(:last-of-type)': {
      borderBottom: 0,
    },
    '&:last-of-type': {
      borderBottomLeftRadius: 8,
      borderBottomRightRadius: 8,
    },
    '&:before': {
      display: 'none',
    },
  }))

  const AccordionSummary = styled((aprops: AccordionSummaryProps) => (
    <MuiAccordionSummary
      expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '1rem' }} />}
      {...aprops}
    />
  ))(() => ({
    backgroundColor: theme.palette.action.hover,
    flexDirection: 'row-reverse',
    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
      transform: 'rotate(90deg)',
    },
    '& .MuiAccordionSummary-content': {
      marginLeft: theme.spacing(1),
    },
  }))

  const AccordionDetails = styled(MuiAccordionDetails)(() => ({
    padding: theme.spacing(2),
    borderTop: `1px solid ${moduleConfig?.fsColor.light}66`,
  }))

  const onCauseClick = (cause: GraphSeries) => {
    const eCId = gapGraphSeries.find((it: GraphSeries) => it.name === cause.name)!.id
    setId(`${gapId}_${eCId}`)
    setCurId(eCId)
  }

  return (
    <Grid container direction="row" display="flex" justifyContent="space-between">
      <Grid size={{ md: 12, lg: 9 }} pr={{ md: 0, lg: 4 }}>
        <>
          <Stack
            direction="row"
            justifyContent="flex-start"
            alignItems="center"
            spacing={2}
            mt={-4}
            mb={2}
          >
            <Stack direction="row" alignItems="center">
              <Box mr={1}>
                {getIcon(moduleConfig?.module ?? '', { color: moduleConfig?.fsColor.primary })}
              </Box>
              {getIcon(diversityDimenstionConfig?.diversityDimension ?? '', {
                color: diversityDimenstionConfig?.fsColor.primary,
              })}
              <Typography variant="h5" ml={1} component="div" className="flex items-end">
                {t(`module.name.${moduleConfig?.module}` as any)} {t('by')}{' '}
                {t(
                  `context.model.name.${
                    gapDescription?.groupOfInterest.groupVariable.name.split('@')[0]
                  }` as any,
                )}
                &nbsp;
                {t('and')}&nbsp;
                {t(
                  `descriptiveCharacteristic.namewu.${gapDescription?.groupOfInterest.descriptiveCharacteristic.name}` as any,
                )}
                <Typography ml={1}>
                  (
                  {translatedDomainName(
                    gapDescription?.groupOfInterest.groupVariable.label ?? '',
                    t,
                  )}{' '}
                  {t('vs.')}{' '}
                  {translatedDomainName(
                    gapDescription?.referenceGroup.groupVariable.label ?? '',
                    t,
                  )}
                  )
                </Typography>
              </Typography>
            </Stack>
          </Stack>
          <Box>
            {!isEmpty(gapDescription) ? (
              <>
                <Accordion
                  expanded={expanded === 'panel4'}
                  onChange={() => (expanded === 'panel4' ? setExpanded('') : setExpanded('panel4'))}
                >
                  <AccordionSummary>
                    <Typography>
                      {t(
                        `dependentCharacteristic.name.${gapDescription?.referenceGroup.descriptions[0].descriptionDependentVariable.characteristic.name}` as any,
                      )}
                      ,&nbsp;
                      {t(
                        `descriptiveCharacteristic.namewu.${gapDescription?.groupOfInterest.descriptiveCharacteristic.name}` as any,
                      )}
                      &nbsp;
                      {t('and')}&nbsp;
                      {t(
                        `context.model.name.${
                          gapDescription?.groupOfInterest.groupVariable.name.split('@')[0]
                        }` as any,
                      )}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {gapDescription ? (
                      <FsDescriptionByCauses
                        gapDetailed={gapDescription}
                        moduleConfig={moduleConfig}
                        diversityDimenstionConfig={diversityDimenstionConfig}
                      />
                    ) : null}
                  </AccordionDetails>
                </Accordion>
                <Accordion
                  expanded={expanded === 'panel1'}
                  onChange={() => (expanded === 'panel1' ? setExpanded('') : setExpanded('panel1'))}
                >
                  <AccordionSummary>
                    <Typography>{t('number.employees')}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {gapDescription ? (
                      <FsDetailedDescriptiveOverview detailedGapDescription={gapDescription} />
                    ) : null}
                  </AccordionDetails>
                </Accordion>
                <Accordion
                  expanded={expanded === 'panel2'}
                  onChange={() => (expanded === 'panel2' ? setExpanded('') : setExpanded('panel2'))}
                >
                  <AccordionSummary>
                    <Typography>
                      {t(
                        `context.model.name.${
                          gapDescription?.groupOfInterest.groupVariable.name.split('@')[0]
                        }` as any,
                      )}{' '}
                      {t('and')}{' '}
                      {t(
                        `descriptiveCharacteristic.namewu.${gapDescription?.groupOfInterest.descriptiveCharacteristic.name}` as any,
                      )}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {gapDescription ? (
                      <FsDescriptionByDiversity
                        detailedGapDescription={gapDescription}
                        moduleConfig={moduleConfig}
                        diversityDimenstionConfig={diversityDimenstionConfig}
                        groupOfInterestPercentage={groupOfInterestPercentage}
                      />
                    ) : null}
                  </AccordionDetails>
                </Accordion>
                <Accordion
                  expanded={expanded === 'panel3'}
                  onChange={() => (expanded === 'panel3' ? setExpanded('') : setExpanded('panel3'))}
                >
                  <AccordionSummary>
                    <Typography>
                      {t(
                        `descriptiveCharacteristic.namewu.${gapDescription?.groupOfInterest.descriptiveCharacteristic.name}` as any,
                      )}{' '}
                      {t('and')}{' '}
                      {t(
                        `context.model.name.${
                          gapDescription?.groupOfInterest.groupVariable.name.split('@')[0]
                        }` as any,
                      )}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {gapDescription ? (
                      <FsDescriptionByColumnPercentage
                        descriptionByColumnPercent={gapDescription}
                        moduleConfig={moduleConfig}
                        diversityDimenstionConfig={diversityDimenstionConfig}
                      />
                    ) : null}
                  </AccordionDetails>
                </Accordion>
                {gapDescription.groupOfInterest.descriptiveCharacteristic.name === 'cwi_groups' && (
                  <Accordion
                    expanded={expanded === 'panel5'}
                    onChange={() =>
                      expanded === 'panel5' ? setExpanded('') : setExpanded('panel5')
                    }
                  >
                    <AccordionSummary>
                      <Typography variant="h6">{t('cwi_groups.exaplain')}</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 3, textAlign: 'justify' }}>
                      {CWIExplain[i18n.language as 'de' | 'en'].content.map((e: any) => (
                        <Typography sx={{ mt: 2 }}>{e}</Typography>
                      ))}
                      <Typography sx={{ mt: 2 }} fontWeight="bold">
                        {t('literature')}:
                      </Typography>
                      {CWIExplain[i18n.language as 'de' | 'en'].literature.map((e: any) => (
                        <Typography sx={{ mt: 2 }}>{e}</Typography>
                      ))}
                    </AccordionDetails>
                  </Accordion>
                )}
              </>
            ) : (
              <FsFullPageLoader />
            )}
          </Box>
        </>
      </Grid>
      <Grid size={{ xs: 3 }} mt={2} visibility={{ md: 'hidden', lg: 'visible' }}>
        <Box
          px={2}
          py={4}
          sx={{
            backgroundColor: theme.palette.background.default,
            border: `1px solid ${moduleConfig?.fsColor.light}66`,
            borderRadius: 2,
          }}
        >
          <Typography mt={0.5} mb={1} variant="subtitle1">
            {t('gap.causes.emptycaption')}
          </Typography>
          {gapGraphSeries.map((gs) => (
            <Stack
              sx={{
                cursor: 'pointer',
                backgroundColor: gs.id === curId ? theme.palette.action.hover : '',
              }}
              onClick={() => onCauseClick(gs)}
              key={gs.name}
            >
              <ListItemButton>
                <Grid container width="100%">
                  <Grid size={{ md: 12, lg: 6 }}>
                    <Typography>{gs.name}</Typography>
                  </Grid>
                  <Grid size={{ md: 12, lg: 6 }}>
                    <Typography
                      variant="body2"
                      style={{ textDecoration: 'underline' }}
                      textAlign="right"
                    >
                      {t('view.details')}
                    </Typography>
                  </Grid>
                </Grid>
              </ListItemButton>
              <Divider />
            </Stack>
          ))}
        </Box>
      </Grid>
    </Grid>
  )
}

export default FsDetailDescription
